// src/services/queries.js
import { useQuery, useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useAPI } from 'services/useAPI';
import toast from 'react-hot-toast';
import handleError from 'utils/handleError';

import { useLoading } from 'contexts/LoadingContext';

function objectToFormData(obj, form = null, namespace = '') {
    const formData = form || new FormData();
    let hasFile = false;

    for (let property in obj) {
        if (obj.hasOwnProperty(property)) {
            const formKey = namespace ? `${namespace}[${property}]` : property;

            if (obj[property] instanceof File) {
                formData.append(formKey, obj[property], obj[property].name);
                hasFile = true;
            } else if (typeof obj[property] === 'object' && obj[property] !== null && !(obj[property] instanceof File)) {
                const [nestedFormData, nestedHasFile] = objectToFormData(obj[property], formData, formKey);
                hasFile = hasFile || nestedHasFile;
            } else if (obj[property] !== undefined && obj[property] !== null) {
                formData.append(formKey, obj[property]);
            } else if (obj[property] === null && property === 'payment_term_template') {
                // Special handling for payment_term_template: send empty string to clear
                formData.append(formKey, '');
            }
        }
    }

    return [formData, hasFile];
}

export const useFetchData = (endpoint, showToast = true, enabled = true) => {
    const api = useAPI();

    return useQuery({
        queryKey: [endpoint],
        queryFn: async () => {
            const { data } = await api.get(endpoint);
            return data?.results ? data.results : data;
        },
        enabled: enabled,
        onError: (error) => {
            console.log(error.message)
            if (showToast) {
                toast.error(handleError(error));
            }
        }
    });
};

export const useFetchDataId = (endpoint, id, showToast = true) => {
    const api = useAPI();

    return useQuery({
        queryKey: [endpoint, id],
        enabled: !!id,
        queryFn: async () => {
            const { data } = await api.get(`${endpoint}/${id}`);
            return data?.results ? data.results : data;
        },
        onError: (error) => {
            console.error(error)
            if (showToast) {
                toast.error(handleError(error));
            }
        }
    });
};

export const useCreateData = (endpoint, queryKeys = [], options = {}) => {
    const api = useAPI();
    const { setLoading } = useLoading();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: endpoint,
        mutationFn: async (newObject) => {
            setLoading(true)
            const [formData, hasFile] = objectToFormData(newObject);
            const { data } = await api.post(`${endpoint}/`, formData, {
                headers: {
                    'Content-Type': hasFile ? 'multipart/form-data' : 'application/json',
                },
            });
            if (options.showNotification !== false) {
                toast.success('Data created successfully');
            }
            return data;
        },
        onSuccess: (data, variables) => {
            // Invalidate provided query keys
            if (queryKeys && queryKeys.length > 0) {
                queryKeys.forEach(key => {
                    queryClient.invalidateQueries({ queryKey: [key] });
                });
            }
            // Also invalidate the endpoint query and any related queries
            queryClient.invalidateQueries({
                queryKey: [endpoint],
                exact: false,
                refetchType: 'all'
            });
            // Also invalidate with trailing slash if endpoint doesn't have one
            if (!endpoint.endsWith('/')) {
                queryClient.invalidateQueries({
                    queryKey: [`${endpoint}/`],
                    exact: false,
                    refetchType: 'all'
                });
            }
            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables);
            }
        },
        onError: (error) => {
            console.log(error.message)
            if (options.showNotification !== false) {
                toast.error(handleError(error));
            }
            // Call custom onError if provided
            if (options.onError) {
                options.onError(error);
            }
            return false
        },
        onSettled: () => setLoading(false),
    });
}

export const useUpdateData = (endpoint, queryKeys = [], options = {}) => {
    const api = useAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: endpoint,
        mutationFn: async ({ id, newData }) => {
            const [formData, hasFile] = objectToFormData(newData);
            const url = id ? `${endpoint}/${id}/` : `${endpoint}/`;
            const { data } = await api.patch(url, formData, {
                headers: {
                    'Content-Type': hasFile ? 'multipart/form-data' : 'application/json',
                },
            });
            if (options.showNotification !== false) {
                toast.success('Data updated successfully');
            }
            return data;
        },
        onSuccess: (data, variables) => {
            // Invalidate provided query keys
            if (queryKeys && queryKeys.length > 0) {
                queryKeys.forEach(key => {
                    queryClient.invalidateQueries({ queryKey: [key] });
                });
            }
            // Also invalidate the endpoint query and any related queries
            queryClient.invalidateQueries({
                queryKey: [endpoint],
                exact: false,
                refetchType: 'all'
            });
            // Also invalidate with trailing slash if endpoint doesn't have one
            if (!endpoint.endsWith('/')) {
                queryClient.invalidateQueries({
                    queryKey: [`${endpoint}/`],
                    exact: false,
                    refetchType: 'all'
                });
            }
            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables);
            }
        },
        onError: (error) => {
            console.error(error);
            if (options.showNotification !== false) {
                toast.error(handleError(error));
            }
            // Call custom onError if provided
            if (options.onError) {
                options.onError(error);
            }
        }
    });
}

export const useDeleteData = (endpoint, queryKeys = [], options = {}) => {
    const api = useAPI();
    const queryClient = useQueryClient();

    // Ensure endpoint has no trailing slash for consistency in building URLs & keys
    const normalizedEndpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;

    return useMutation({
        mutationKey: normalizedEndpoint,
        // Accept only primitive id (string | number) as per reverted API contract
        mutationFn: async (id) => {
            const { data } = await api.delete(`${normalizedEndpoint}/${id}/`);
            if (options.showNotification !== false) {
                toast.success('Data deleted successfully');
            }
            return data;
        },
        onSuccess: (data, id) => {
            // Invalidate provided query keys
            if (queryKeys && queryKeys.length > 0) {
                queryKeys.forEach(key => {
                    queryClient.invalidateQueries({ queryKey: [key] });
                });
            }
            // Invalidate both with and without trailing slash (defensive for existing queries)
            [normalizedEndpoint, `${normalizedEndpoint}/`].forEach(key => {
                queryClient.invalidateQueries({
                    queryKey: [key],
                    exact: false,
                    refetchType: 'all'
                });
            });
            if (options.onSuccess) {
                options.onSuccess(data, id);
            }
        },
        onError: (error) => {
            console.log(error);
            if (options.showNotification !== false) {
                toast.error(handleError(error));
            }
            if (options.onError) {
                options.onError(error);
            }
        }
    });
};


export const useDownloadData = (endpoint) => {
    const { setLoading } = useLoading();

    const api = useAPI();

    return useMutation({
        mutationKey: ['download', endpoint],
        mutationFn: async (filenameFallback = 'download.pdf') => {
            setLoading(true)
            try {
                const response = await api.get(endpoint, { responseType: 'blob' });
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                const url = window.URL.createObjectURL(blob);


                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;

                let filename = filenameFallback;
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                    filename = filenameMatch ? filenameMatch[1] : filenameFallback;
                }

                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('File downloaded successfully!');

                return { filename };
            } catch (error) {
                const response = await api.get(endpoint);
                console.log(response)
            }
        },
        onError: (error) => {
            console.error(error);
            toast.error(handleError(error));
        },
        onSettled: () => setLoading(false),
    });
};


export const useFetchActionData = (endpoint, options = {}) => {
    const api = useAPI();

    return useQuery({
        queryKey: [`action-${endpoint}`],
        queryFn: async () => {
            const { data } = await api.get(endpoint, options);
            return data?.results ? data.results : data;
        },
        enabled: !!endpoint,
        onError: (error) => {
            console.error("Error fetching action data:", error);
        }
    });
};